import { Component, inject, signal, computed, viewChild, OnInit, PLATFORM_ID } from '@angular/core';
import { brandColor } from '../../../services/theme';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';

import {
  PaymentService,
  CreateRazorpayOrder,
  RazorpayCheckout,
  OFFLINE_PAYMENT_MODES,
  postToGateway,
  gatewayLabel,
} from '../../../services/payment';
import { ReceiptView } from '../receipt/receipt-view';
import { CoiView } from '../../policies/coi-view/coi-view';
import { RazorpayService, RazorpaySuccess } from '../../../services/razorpay';
import { MemberService } from '../../../services/member';
import { DropdownService, DropdownItem } from '../../../services/dropdown';
import { ToastService } from '../../../services/toast';
import { PageTitleService } from '../../../services/page-title';
import { AuthService } from '../../../services/auth';
import { OtpStep } from '../../member/otp-step';
import { MemberEmailDialog } from '../../../components/member-email-dialog/member-email-dialog';

@Component({
  selector: 'app-collect-payment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CardModule,
    SelectModule,
    ButtonModule,
    TagModule,
    InputTextModule,
    InputNumberModule,
    DialogModule,
    OtpStep,
    ReceiptView,
    CoiView,
    MemberEmailDialog,
  ],
  templateUrl: './collect-payment.html',
  styleUrls: ['./collect-payment.css'],
})
export class CollectPaymentPage implements OnInit {
  private fb = inject(FormBuilder);
  private payment = inject(PaymentService);
  private rzp = inject(RazorpayService);
  private members = inject(MemberService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  /** Only admins see the "All Payments" ledger link (partners are blocked from that route). */
  readonly isAdmin = this.auth.isAdmin();

  memberId = 0;
  loadingMember = signal(false);
  member = signal<any | null>(null);
  /** Guards the one-shot auto-open of Razorpay once the member + amount are ready. */
  private autoPayTried = false;

  /**
   * Where the page is in the verify→pay flow:
   *   'otp' — member not KYC-verified yet: verify via OTP first, then collect.
   *   'pay' — member verified & unpaid: collect the premium (partner auto-opens Razorpay).
   * Plus `alreadyPaid` (nothing to collect) and the existing `done` (just collected).
   */
  stage = signal<'otp' | 'pay'>('pay');
  alreadyPaid = signal(false);

  // ---- OTP step inputs, populated when an unverified member needs verifying ----
  sendingOtp = signal(false);
  otpSent = signal(true);
  otpChannels = signal<string[]>([]);
  otpExpiry = signal(5);
  otpMessage = signal('');

  processing = signal(false);
  done = signal(false); // PAID success state
  /** The method the gateway reported (captured by the backend) — shown on the done state. */
  paidMode = signal<string | null>(null);
  private paymentUid = '';
  /** Numeric payment id (from CreateOrder/Create) — fallback for opening the receipt. */
  private paymentDbId = 0;

  // Receipt auto-shown on success (the backend hands back a `receipt` block on PAID).
  showReceipt = signal(false);
  receiptPaymentId = signal<number | null>(null);

  // Certificate of Insurance — the backend also returns a `coi` block on PAID (policy issued).
  showCoi = signal(false);
  coiMemberId = signal<number | null>(null);

  modeOptions = signal<DropdownItem[]>([]);
  payerOptions = signal<DropdownItem[]>([]);
  partnerOptions = signal<DropdownItem[]>([]);

  memberName = computed(() => {
    const m = this.member();
    if (!m) return '';
    return (
      m.memberName ||
      [m.firstName, m.middleName, m.lastName].filter(Boolean).join(' ') ||
      m.fullName ||
      `Member #${this.memberId}`
    );
  });

  memberUid = computed(() => {
    const m = this.member();
    return m?.memberUniqueId || m?.memberSeriesNo || '';
  });

  /** Premium breakup + other member columns, shown above the amount. */
  premium = computed(() => {
    const m = this.member();
    const n = (...keys: string[]) => {
      for (const k of keys) {
        const v = Number(m?.[k]);
        if (Number.isFinite(v)) return v;
      }
      return 0;
    };
    return {
      base: n('basePrimume', 'BasePrimume', 'basePremium', 'BasePremium'),
      gstPer: n('gstPer', 'GstPer', 'gstPercentage', 'GstPercentage'),
      gstAmt: n('gstAmt', 'GstAmt', 'gstAmount', 'GstAmount'),
      total: this.premiumTotal(m),
      sumInsured: n('sumInsured', 'SumInsured'),
      doctorAmt: n('doctorConsultantAMT', 'DoctorConsultantAMT'),
      policy: m?.masterPolicyName ?? m?.MasterPolicyName ?? '',
      coverage: m?.coverageType ?? m?.CoverageType ?? '',
      mobile: m?.mobileNumber ?? m?.MobileNumber ?? '',
      email: m?.email ?? m?.Email ?? '',
    };
  });

  /** The amount to collect — the member's premium incl. GST, falling back to gross. */
  private premiumTotal(m: any): number {
    for (const k of ['premiumWithGST', 'PremiumWithGST', 'grossAmt', 'GrossAmt']) {
      const v = Number(m?.[k]);
      if (Number.isFinite(v) && v > 0) return v;
    }
    return 0;
  }

  payForm: FormGroup = this.fb.group({
    memberId: [0, Validators.required],
    amount: [null, [Validators.required, Validators.min(0.01)]],
    currency: ['INR'],
    paymentMode: [null],
    payerType: ['CLIENT', Validators.required],
    partnerId: [null],
    clientRef: [null],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.memberId = Number(this.route.snapshot.paramMap.get('memberId')) || 0;
    this.payForm.patchValue({ memberId: this.memberId });

    const qAmount = Number(this.route.snapshot.queryParamMap.get('amount'));
    if (Number.isFinite(qAmount) && qAmount > 0) this.payForm.get('amount')!.setValue(qAmount);

    this.payForm.get('payerType')!.valueChanges.subscribe((t) => {
      if (t === 'PARTNER') this.payForm.get('clientRef')!.setValue(null, { emitEvent: false });
      else this.payForm.get('partnerId')!.setValue(null, { emitEvent: false });
    });

    this.loadLookups();
    this.dropdown.partners().subscribe((o) => this.partnerOptions.set(o));
    this.rzp.load(); // warm the Checkout script
    if (this.memberId) {
      this.loadMember();
      this.loadMethod();
    }
  }

  get isPartnerPayer(): boolean {
    return this.payForm.get('payerType')?.value === 'PARTNER';
  }

  /** True when the chosen mode is settled offline (cash/cheque) — no gateway at all. */
  get isOffline(): boolean {
    return OFFLINE_PAYMENT_MODES.includes((this.payForm.get('paymentMode')?.value || '').toUpperCase());
  }

  private loadLookups() {
    const toOpts = (rows: any[]): DropdownItem[] =>
      (rows || [])
        .filter((r) => r.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((r) => ({ value: r.typeCode, label: r.typeName }));

    this.payment.ptByCategory('PAYMENT_MODE').subscribe((res: any) => this.modeOptions.set(toOpts(res?.data ?? [])));
    this.payment.ptByCategory('PAYER_TYPE').subscribe((res: any) => {
      const opts = toOpts(res?.data ?? []);
      this.payerOptions.set(
        opts.length ? opts : [
          { value: 'PARTNER', label: 'Partner' },
          { value: 'CLIENT', label: 'Client' },
        ],
      );
    });
  }

  private loadMember() {
    this.loadingMember.set(true);
    this.members.getById(this.memberId).subscribe({
      next: (res: any) => {
        this.loadingMember.set(false);
        const m = res?.data ?? res ?? null;
        this.member.set(m);
        // Amount = the member's premium with GST (data.PremiumWithGST), gross as a fallback.
        const amt = this.premiumTotal(m);
        if (Number.isFinite(amt) && amt > 0 && this.payForm.get('amount')!.value == null) {
          this.payForm.get('amount')!.setValue(amt);
        }
        this.routeStage(m);
      },
      error: () => this.loadingMember.set(false),
    });
  }

  private isVerified(m: any): boolean {
    return (m?.kycStatus ?? m?.KycStatus ?? '').toString().toUpperCase() === 'VERIFIED';
  }
  private isPaid(m: any): boolean {
    return (m?.paymentStatus ?? m?.PaymentStatus ?? '').toString().toUpperCase() === 'PAID';
  }

  /**
   * Decide the flow once the member is loaded:
   *   already paid  → nothing to collect;
   *   not verified  → verify with OTP first (payment is blocked until then);
   *   verified+unpaid → go straight to collecting the premium.
   */
  private routeStage(m: any) {
    if (this.isPaid(m)) {
      this.alreadyPaid.set(true);
      return;
    }
    if (!this.isVerified(m)) {
      this.enterOtp();
      return;
    }
    this.stage.set('pay');
    // Do NOT auto-charge on the member's stale UNPAID flag. If a previous attempt was captured
    // at Razorpay but its /Verify never landed, the member still reads UNPAID — and auto-pay
    // would take the money a second time. Ask the gateway about any open payment first.
    this.reconcileThenAutoPay();
  }

  /**
   * Settle any still-open payment for this member before we consider charging again.
   * GET /MemberPayment/GetByPaymentUniqueId runs ReconcileIfPaid server-side, which asks Razorpay
   * whether the order was actually captured and confirms it if so. If one flips to SUCCESS we show
   * "already paid" instead of opening a second checkout.
   */
  private reconcileThenAutoPay() {
    this.payment.payGetByMember(this.memberId).subscribe({
      next: (res: any) => {
        const rows: any[] = res?.data ?? [];
        const open = rows.filter((r) => {
          const st = (r?.paymentStatus ?? '').toString().toUpperCase();
          return (st === 'PENDING' || st === 'INITIATED') && !!r?.paymentUniqueId;
        });
        if (!open.length) {
          this.maybeAutoPay();
          return;
        }
        // Newest first: the most recent attempt is the likeliest to hold the money.
        const uids = open
          .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0))
          .map((r) => r.paymentUniqueId as string);
        this.reconcileNext(uids, 0);
      },
      // Never let a lookup failure block collection — fall back to the normal flow.
      error: () => this.maybeAutoPay(),
    });
  }

  private reconcileNext(uids: string[], i: number) {
    if (i >= uids.length) {
      this.maybeAutoPay();
      return;
    }
    this.payment.payGetByUid(uids[i]).subscribe({
      next: (res: any) => {
        const st = (res?.data?.paymentStatus ?? '').toString().toUpperCase();
        if (st === 'SUCCESS') {
          // The gateway had the money all along; the server has now confirmed and issued.
          this.alreadyPaid.set(true);
          this.stage.set('pay');
          this.toast.success('This member had already paid — the payment has now been confirmed.');
          return;
        }
        this.reconcileNext(uids, i + 1);
      },
      error: () => this.reconcileNext(uids, i + 1),
    });
  }

  /** Send a fresh OTP to the member, then show the OTP step for verification. */
  private enterOtp() {
    this.stage.set('otp');
    this.sendingOtp.set(true);
    this.members.resendOtp(this.memberId).subscribe({
      next: (res: any) => {
        this.sendingOtp.set(false);
        const d = res?.data ?? {};
        this.otpChannels.set(d?.otp?.channels ?? d?.channels ?? []);
        this.otpExpiry.set(d?.otp?.expiresInMinutes ?? d?.expiresInMinutes ?? 5);
        this.otpMessage.set(d?.otpMessage ?? res?.message ?? '');
        this.otpSent.set(res?.success !== false);
        if (res?.success === false) this.toast.error(res?.message || 'Could not send OTP.');
      },
      error: () => {
        this.sendingOtp.set(false);
        this.otpSent.set(false);
        this.toast.error('Could not send the OTP. Use “Resend” to try again.');
      },
    });
  }

  /** OTP verified — mark the member verified locally and move on to collecting payment. */
  onOtpVerified() {
    this.member.set({ ...(this.member() ?? {}), kycStatus: 'VERIFIED' });
    this.stage.set('pay');
    this.autoPayTried = false;
    this.maybeAutoPay();
  }

  /**
   * Direct payment open: once the member + amount are ready, kick off the online checkout
   * automatically so the partner lands straight on the Razorpay popup. Fires once, online only
   * (an offline mode like cash needs the form), and the manual Pay button remains as a fallback.
   */
  private maybeAutoPay() {
    if (this.autoPayTried || this.done() || this.processing() || this.stage() !== 'pay') return;
    // Admins keep the full form (payer/mode choices); partners get the streamlined direct open.
    if (this.isAdmin) return;
    const amt = Number(this.payForm.get('amount')?.value);
    if (!Number.isFinite(amt) || amt <= 0 || this.isOffline) return;
    this.autoPayTried = true;
    this.payOnline();
  }

  // ===================== pay =====================
  pay() {
    // A click can land before the async handlers flip processing — never dispatch twice
    // (the backend has no idempotency on CreateOrder, so a double fire = a double payment row).
    if (this.processing()) return;
    if (this.payForm.invalid) {
      this.payForm.markAllAsTouched();
      if (this.payForm.get('amount')?.invalid) this.toast.warning('Enter an amount greater than 0.');
      else if (this.payForm.get('payerType')?.invalid) this.toast.warning('Payer type is required.');
      else this.toast.warning('Please complete the required fields.');
      return;
    }
    if (this.isOffline) this.payOffline();
    else this.payOnline();
  }

  /** Opened when a gateway refuses the payment for want of an e-mail. */
  private emailDialog = viewChild(MemberEmailDialog);

  /** The e-mail was saved — carry straight on with the payment that needed it. */
  onEmailSaved(email: string) {
    const m = this.member();
    if (m) this.member.set({ ...m, email });
    this.payOnline();
  }

  /** Which gateway will actually open, so the page names it instead of assuming Razorpay. */
  gateway = signal<string>('');
  gatewayRedirect = signal(false);

  payLabel = computed(() => {
    if (this.isOffline) return 'Record Payment';
    const name = gatewayLabel(this.gateway());
    return name ? `Pay with ${name}` : 'Pay now';
  });

  private loadMethod() {
    if (this.memberId <= 0) return;
    this.payment.payMethod(this.memberId).subscribe({
      next: (res: any) => {
        const d = res?.data;
        if (!d) return;
        this.gateway.set(d.gateway ?? '');
        this.gatewayRedirect.set(!!d.redirect);
      },
      // Unknown gateway just means a name-free button — never block paying over it.
      error: () => {},
    });
  }

  private orderBody(): CreateRazorpayOrder {
    const v = this.payForm.getRawValue();
    const partner = v.payerType === 'PARTNER';
    return {
      memberId: this.memberId,
      amount: Number(v.amount),
      currency: (v.currency || 'INR').toString().trim().toUpperCase(),
      paymentMode: v.paymentMode || null,
      payerType: v.payerType,
      partnerId: partner ? (v.partnerId ?? null) : null,
      clientRef: partner ? null : this.blankToNull(v.clientRef),
    };
  }

  // ----- online (whichever gateway the member's partner is assigned) -----
  private payOnline() {
    if (this.processing()) return;
    // Checkout prefills the member's email/mobile — if the member never loaded (e.g. the
    // lookup failed but ?amount= pre-filled the form), stop instead of opening a broken popup.
    if (!this.member()) {
      this.toast.error('Member details could not be loaded. Refresh and try again.');
      return;
    }

    // The gateway is the SERVER's decision, from the partner's assignment — not this form's.
    // Posting straight to Razorpay is why assigning a partner the Generali integration appeared
    // to do nothing. The amount goes the same way: Create charges the member's own premium.
    this.processing.set(true);
    this.payment
      .payStart({ memberId: this.memberId, paymentMode: this.payForm.getRawValue().paymentMode || null })
      .subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.processing.set(false);

            // The gateway needs an e-mail this member doesn't have — capture it here instead of
            // sending the operator off to edit the member and come back.
            if (res?.data?.needsEmail) {
              this.emailDialog()?.open();
              return;
            }

            this.toast.error(res?.message || 'Could not start the payment.');
            return;
          }
          const d = res?.data ?? {};

          // A redirect gateway takes the customer off-site and settles on its own callback;
          // there is no Checkout handler to come back through, so this page ends here.
          if (String(d.gateway || '').toUpperCase() === 'GENERALI') {
            if (!postToGateway(d.postUrl, d.fields)) {
              this.processing.set(false);
              this.toast.error('The payment gateway did not return a valid redirect.');
            }
            return;
          }

          this.openCheckout(d as RazorpayCheckout);
        },
        error: () => {
          this.processing.set(false);
          this.toast.error('Could not start the payment.');
        },
      });
  }

  private openCheckout(co: RazorpayCheckout) {
    if (!co?.orderId || !co?.keyId) {
      this.processing.set(false);
      this.toast.error('Payment could not be initialised.');
      return;
    }
    const m = this.member();
    this.paymentUid = co.paymentUniqueId;
    this.paymentDbId = co.paymentId;
    this.rzp.load().then((ok) => {
      if (!ok) {
        this.processing.set(false);
        this.toast.error('Could not load Razorpay Checkout. Check your connection.');
        return;
      }
      this.rzp.open({
        key: co.keyId,
        order_id: co.orderId,
        amount: co.amount,
        currency: co.currency,
        name: co.name,
        description: co.description,
        prefill: { name: this.memberName(), email: m?.email, contact: m?.mobileNumber },
        theme: { color: brandColor() },
        handler: (r) => this.verify(r),
        modal: { ondismiss: () => this.onDismiss() },
        onFailed: (e) => {
          this.processing.set(false);
          this.toast.error(e?.error?.description || 'Payment failed.');
        },
      });
    });
  }

  private verify(r: RazorpaySuccess) {
    this.payment
      .rzpVerify({
        razorpayOrderId: r.razorpay_order_id,
        razorpayPaymentId: r.razorpay_payment_id,
        razorpaySignature: r.razorpay_signature,
      })
      .subscribe({
        next: (res: any) => {
          this.processing.set(false);
          const memberStatus = res?.data?.memberPaymentStatus ?? res?.data?.MemberPaymentStatus;
          if (res?.success && memberStatus === 'PAID') {
            this.done.set(true);
            this.toast.popup(`${this.memberName()} is now insured (PAID).`, 'Payment successful', 'success');
            this.fetchPaidMode();
            this.openReceipt(res?.data?.receipt, this.paymentDbId);
            this.setCoi(res?.data?.coi);
          } else {
            this.reconcileAfterFailedVerify(r.razorpay_payment_id, res?.message);
          }
        },
        error: () => {
          this.processing.set(false);
          this.reconcileAfterFailedVerify(r.razorpay_payment_id);
        },
      });
  }

  /**
   * Verify failed, but the customer may still have been charged. Ask the server to re-check with
   * the gateway (ReconcileIfPaid) before telling anyone the payment did not go through — otherwise
   * the member reads UNPAID and the partner collects a second time.
   */
  private reconcileAfterFailedVerify(gatewayPaymentId?: string, verifyMessage?: string) {
    const warn = () =>
      this.toast.error(
        gatewayPaymentId
          ? `Payment WAS taken (ref ${gatewayPaymentId}) but could not be confirmed. ` +
              'Do NOT collect again — quote this reference to support.'
          : verifyMessage || 'Payment verification failed.',
      );

    if (!this.paymentUid) {
      warn();
      return;
    }

    this.payment.payGetByUid(this.paymentUid).subscribe({
      next: (res: any) => {
        if ((res?.data?.paymentStatus ?? '').toString().toUpperCase() !== 'SUCCESS') {
          warn();
          return;
        }
        this.done.set(true);
        this.toast.popup(`${this.memberName()} is now insured (PAID).`, 'Payment successful', 'success');
        this.fetchPaidMode();
        this.openReceipt(null, this.paymentDbId);
        this.setCoi(null);
      },
      error: () => warn(),
    });
  }

  private onDismiss() {
    this.processing.set(false);
    this.toast.info('Payment not completed. If money was debited it will confirm shortly.');
  }

  /** Read back the payment to show the method the gateway reported (UPI / card / …). */
  private fetchPaidMode() {
    if (!this.paymentUid) return;
    this.payment.payGetByUid(this.paymentUid).subscribe({
      next: (res: any) => {
        const mode = res?.data?.paymentMode ?? res?.data?.PaymentMode;
        if (mode) this.paidMode.set(mode);
      },
    });
  }

  // ----- offline (cash / cheque) -----
  private payOffline() {
    if (this.processing()) return;
    const v = this.orderBody();
    const body: any = { ...v, paymentStatus: 'INITIATED' };
    this.processing.set(true);
    this.payment.payCreate(body).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.processing.set(false);
          this.toast.error(res?.message || 'Could not record the payment.');
          return;
        }
        const d = res?.data ?? {};
        const uid = d.paymentUniqueId ?? d.PaymentUniqueId;
        if (!uid) {
          // Without the reference id the status update below would 400 and the payment
          // would sit in INITIATED with no explanation — stop with a clear message instead.
          this.processing.set(false);
          this.toast.error('Payment was recorded but no reference id came back. Please verify it in All Payments.');
          return;
        }
        this.paymentDbId = Number(d.paymentId ?? d.PaymentId) || this.paymentDbId;
        this.payment.payUpdateStatus({ paymentUniqueId: uid, paymentStatus: 'SUCCESS' }).subscribe({
          next: (r2: any) => {
            this.processing.set(false);
            if (r2?.success === false) {
              this.toast.error(r2?.message || 'Could not confirm the payment.');
              return;
            }
            const ms = r2?.data?.memberPaymentStatus ?? r2?.data?.MemberPaymentStatus;
            this.paidMode.set(this.payForm.get('paymentMode')?.value || null);
            this.done.set(true);
            this.toast.popup(
              ms === 'PAID' ? `${this.memberName()} is now insured (PAID).` : 'Payment recorded.',
              ms === 'PAID' ? 'Payment successful' : 'Done',
              'success',
            );
            if (ms === 'PAID') {
              const pid = Number(r2?.data?.paymentId ?? r2?.data?.PaymentId) || this.paymentDbId;
              this.openReceipt(r2?.data?.receipt, pid);
              this.setCoi(r2?.data?.coi);
            }
          },
          error: () => this.processing.set(false),
        });
      },
      error: () => this.processing.set(false),
    });
  }

  /**
   * Open the receipt dialog for a just-PAID payment. Prefers the backend's `receipt` block
   * (camel/Pascal tolerant); falls back to the numeric payment id we already hold from the
   * order/create step, so the receipt still shows even if the block is absent.
   */
  private openReceipt(receipt: any, fallbackId: number) {
    const pid = Number(receipt?.paymentId ?? receipt?.PaymentId ?? fallbackId) || 0;
    const available = receipt?.available ?? receipt?.Available;
    if (pid && available !== false) {
      this.receiptPaymentId.set(pid);
      this.showReceipt.set(true);
    }
  }

  /**
   * Stamp the just-issued member's id so the "View Certificate" action can open the COI dialog.
   * Prefers the backend's `coi` block (camel/Pascal tolerant); falls back to the current member.
   */
  private setCoi(coi: any) {
    const mid = Number(coi?.memberId ?? coi?.MemberId ?? this.memberId) || 0;
    if (mid) this.coiMemberId.set(mid);
  }

  /** Reopen the receipt dialog from the done state. */
  openReceiptDialog() {
    if (this.receiptPaymentId()) this.showReceipt.set(true);
  }

  /** Open the certificate dialog from the done state. */
  openCoiDialog() {
    if (this.coiMemberId()) this.showCoi.set(true);
  }

  // ===================== nav =====================
  goToList() {
    this.router.navigate(['/payments/list']);
  }

  private blankToNull(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }
}
